// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import {
  Box,
  Button,
  Typography,
  Stack,
  Avatar,
  Chip,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { useSelector } from 'react-redux';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/ChatIcons/closeIcon.svg';
import { ReactComponent as CheckIcon } from '../../../../assets/Icons/ChatIcons/checkIcon.svg';

import TextEditor from '../TextEditor/TextEditor';
import { isValueEmpty } from '../../../../utils/Utils';
import SwalToast from '../../swalToast';
import { extractEntityMapFromTipTapJson } from '../TextEditor/utils/draftTipTapConverter';
import {
  DO_MMMM_YYYY_DATE_FORMAT_HH_MM,
  NOT_AUTHORIZED_TO_ACCESS_MESSAGE,
} from '../../../../const/CommonConst';

const EmptyArray = [];

const getUserName = (createdBy) => {
  if (!isEmpty(createdBy?.username)) {
    return createdBy?.username;
  }
  return isEmpty(createdBy?.first_name) ? '-' : createdBy?.first_name;
};

/**
 * Normalizes edited Chat Forum note content into the payload shape expected by
 * the notes API.
 *
 * During edit, the Tiptap editor content can be a parsed document object,
 * stringified Tiptap JSON, or empty/null content. The backend expects `message`
 * to be an object and expects mention metadata under `message.entityMap`,
 * matching the create-note POST payload shape. This helper parses string
 * content when needed and preserves an existing `entityMap`; otherwise it
 * attaches the entity map extracted from the current Tiptap document.
 *
 * If parsing fails, it returns a safe object containing `entityMap` so the
 * edit/save flow does not crash on malformed content.
 *
 * @param {object|string|null|undefined} message - Tiptap message content from the editor.
 * @param {object} entityMap - Mention metadata extracted from the Tiptap document.
 * @returns {object} Notes API message payload with `entityMap` included.
 */
const normalizeNoteMessagePayload = (message, entityMap = {}) => {
  if (message === null || message === undefined || message === '') {
    return { entityMap };
  }

  if (typeof message !== 'string') {
    return {
      ...message,
      entityMap: message?.entityMap ?? entityMap,
    };
  }

  try {
    const parsedMessage = JSON.parse(message);
    return {
      ...parsedMessage,
      entityMap: parsedMessage?.entityMap ?? entityMap,
    };
  } catch {
    return { entityMap };
  }
};

const CancelThreadButton = ({
  handleChangeSelectedThread,
  setContent,
  commentData,
}) => {
  const handleCancelThread = useCallback(() => {
    handleChangeSelectedThread(null);
    setContent(commentData?.message || '');
  }, [handleChangeSelectedThread, setContent, commentData]);

  return (
    <Button
      onClick={handleCancelThread}
      size="small"
      variant="text"
      aria-label="Cancel thread"
      sx={{ height: 1, minWidth: 2 }}
    >
      <CloseIcon />
    </Button>
  );
};

CancelThreadButton.propTypes = {
  handleChangeSelectedThread: PropTypes.func.isRequired,
  setContent: PropTypes.func.isRequired,
  commentData: PropTypes.shape({
    message: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }).isRequired,
};
const SubmitThreadButton = ({
  handleChangeSelectedThread,
  commentData,
  index,
  handleSubmit,
}) => {
  const handleClick = useCallback(() => {
    handleChangeSelectedThread({ ...commentData, index });
    handleSubmit();
  }, [handleChangeSelectedThread, handleSubmit, commentData, index]);

  return (
    <Button
      size="small"
      onClick={handleClick}
      aria-label="Submit thread"
      sx={{
        height: 1,
        minWidth: 2,
      }}
    >
      <CheckIcon />
    </Button>
  );
};

SubmitThreadButton.propTypes = {
  handleChangeSelectedThread: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  commentData: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

const ThreadActionButton = ({ label, onClick, isAuthorized = true, title }) => {
  return (
    <Typography
      variant="body3"
      disabled={!isAuthorized}
      onClick={isAuthorized ? onClick : undefined}
      title={!isAuthorized ? NOT_AUTHORIZED_TO_ACCESS_MESSAGE : title || label}
      aria-label={
        !isAuthorized ? NOT_AUTHORIZED_TO_ACCESS_MESSAGE : title || label
      }
      className={`${!isAuthorized ? 'disabledTextColor' : ''}`}
      sx={{
        '&:hover': isAuthorized
          ? {
              cursor: 'pointer',
              opacity: 1,
            }
          : {},
        opacity: 0.9,
      }}
    >
      {label}
    </Typography>
  );
};

ThreadActionButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};

const ThreadMessage = ({
  commentData,
  selectedThread,
  transitionStates = EmptyArray,
  index,
  userList,
  handleChangeSelectedThread,
  deleteNoteHandler,
  handleOpenThreadChatPanel,
  isComponentFromLifecycleEvents = false,
  entityType,
  entityId,
  editNote,
  getAllComments,
  disableTagAndTitleFields,
  isUserAuthorizedForDeleteNote,
  isUserAuthorizedForUpdateNote,
  replyThread = true,
}) => {
  const { profileData, userData } = useSelector((state) => state.users);
  const [showFull, setShowFull] = useState(false);
  const isMessageOwner =
    userData.username === commentData?.created_by?.username;
  const handleToggleText = useCallback(
    () => setShowFull((prev) => !prev),
    [setShowFull]
  );

  const shortText = useMemo(
    () => commentData?.title?.slice(0, 100),
    [commentData?.title]
  );

  const theme = useTheme();

  const [content, setContent] = useState('');

  const threadFormEditValidationSchemaEdit = object({
    title: string()
      .max(100, 'Only 100 characters are allowed')
      .required('required'),
    tag: string().max(100, 'Only 100 characters are allowed').nullable(),
  });

  const threadFormEditInitialValues = useMemo(() => {
    const initialValues = {
      title: '',
      tag: '',
    };

    if (selectedThread) {
      return {
        title: selectedThread?.title || '',
        tag: selectedThread?.tag || '',
      };
    }

    return initialValues;
  }, [selectedThread]);

  const threadForm = useFormik({
    initialValues: threadFormEditInitialValues,
    enableReinitialize: true,
    validationSchema: disableTagAndTitleFields
      ? null
      : threadFormEditValidationSchemaEdit,
    onSubmit: (values) => {
      const entityMap = extractEntityMapFromTipTapJson(content);
      const message = normalizeNoteMessagePayload(content, entityMap);
      const body = {
        message,
        entity_map: entityMap,
        users: [],
        entity_type: entityType,
        entity_id: entityId,
        created_by: { ...profileData, username: userData.username },
        note_id: selectedThread?.note_id,
        tag: values?.tag,
        title: values?.title,
      };

      if (disableTagAndTitleFields) {
        body.tags = null;
        body.title = null;
      }

      editNote(body).then((res) => {
        if (res) {
          SwalToast({
            icon: 'success',
            title: 'Note edited successfully.',
          });
          if (!disableTagAndTitleFields) {
            getAllComments({ entity_id: entityId, entity_type: entityType });
          }
          handleChangeSelectedThread(null);
        }
      });
    },
  });

  const handleEditNote = useCallback(() => {
    if (!isUserAuthorizedForUpdateNote) return;
    handleChangeSelectedThread({ ...commentData, index });
    setContent(commentData?.message || '');
  }, [
    commentData,
    index,
    handleChangeSelectedThread,
    isUserAuthorizedForUpdateNote,
  ]);

  const handleDeleteNote = useCallback(() => {
    if (!isUserAuthorizedForDeleteNote) return;
    deleteNoteHandler(commentData?.note_id);
  }, [deleteNoteHandler, commentData?.note_id, isUserAuthorizedForDeleteNote]);

  const handleReplyNote = useCallback(() => {
    handleChangeSelectedThread({ ...commentData });
    setContent(commentData?.message || '');
    handleOpenThreadChatPanel();
  }, [handleChangeSelectedThread, commentData, handleOpenThreadChatPanel]);

  const renderReplyButton = () => {
    if (
      commentData?.parent_note_id ||
      isComponentFromLifecycleEvents ||
      !replyThread
    ) {
      return null;
    }

    return (
      <Typography
        variant="body3"
        title={commentData?.replies_count > 0 ? 'replies' : 'reply'}
        aria-label={commentData?.replies_count > 0 ? 'replies' : 'reply'}
        sx={{
          '&:hover': {
            cursor: 'pointer',
            opacity: 1,
          },
          opacity: 0.9,
        }}
        disabled={!isUserAuthorizedForUpdateNote}
        onClick={isUserAuthorizedForUpdateNote ? handleReplyNote : undefined}
        className={`${!isUserAuthorizedForUpdateNote ? 'disabledTextColor' : ''}`}
      >
        {commentData?.replies_count > 0
          ? `${commentData?.replies_count} Replies`
          : 'Reply'}
      </Typography>
    );
  };

  useEffect(() => {
    setContent(commentData?.message || '');
  }, [commentData]);
  const handleSubmit = useCallback(() => {
    threadForm?.handleSubmit();
  }, [threadForm]);

  const renderReadModeActions = () => {
    if (selectedThread?.index === index) {
      return null;
    }

    return (
      <Stack direction="row" spacing={2}>
        {isMessageOwner && (
          <ThreadActionButton
            label="Edit"
            title="Edit"
            isAuthorized={isUserAuthorizedForUpdateNote}
            onClick={handleEditNote}
          />
        )}
        {isMessageOwner && (
          <ThreadActionButton
            label="Delete"
            title="Delete"
            isAuthorized={isUserAuthorizedForDeleteNote}
            isMessageOwner={isMessageOwner}
            onClick={handleDeleteNote}
          />
        )}

        {/* This replies will be shown only in thread */}
        {renderReplyButton()}
      </Stack>
    );
  };

  const updatedCommentData = useMemo(
    () => (commentData?.title?.length > 100 ? '...' : ''),
    [commentData]
  );

  return (
    <Box display="flex" gap={2} maxWidth="1000px">
      <Box>
        <Stack direction="row" spacing={2} sx={{ display: 'flex' }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
            }}
          >
            {commentData?.created_by?.username?.slice(0, 1)?.toUpperCase()}
          </Avatar>
        </Stack>
      </Box>

      <Stack
        id={`chatbox-${commentData?.note_id}`}
        flex={1}
        borderRadius={2}
        border="1px solid"
        borderColor={theme?.palette?.other?.borderColor}
        overflow="hidden"
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            borderBottom: '1px solid',
            borderColor: theme?.palette?.other?.borderColor,
            backgroundColor: theme?.palette?.other?.hoverBackground,
            display: 'flex',
            p: '4px 8px',
            mb: selectedThread?.index === index ? 2 : 0,
          }}
        >
          <Typography variant="subtitle1" lineHeight="26px" mb={0.5}>
            {getUserName(commentData?.created_by)}
          </Typography>
          <Typography variant="body4" lineHeight="26px">
            {moment(new Date(commentData?.created_date)).format(
              DO_MMMM_YYYY_DATE_FORMAT_HH_MM
            )}
          </Typography>
          {!isValueEmpty(commentData?.tag) && (
            <Chip
              id="thread-message-tag-chip"
              className="thread-chip"
              label={commentData?.tag}
              size="small"
            />
          )}
        </Stack>
        <Stack p="4px 8px">
          {selectedThread?.index === index ? null : (
            <Stack direction="row">
              <Typography variant="h4" color="other.threadTitle" fontSize={14}>
                {showFull
                  ? commentData?.title
                  : `${shortText ?? ''}${updatedCommentData}`}
              </Typography>

              {commentData?.title?.length > 100 && (
                <Typography
                  onClick={handleToggleText}
                  sx={{
                    cursor: 'pointer',
                    ml: 1,
                    color: 'primary',
                    fontSize: 12,
                  }}
                >
                  {showFull ? 'See less' : 'See more'}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="column">
            <Box>
              {selectedThread?.index === index ? (
                <Box pr={2}>
                  <TextEditor
                    userList={userList}
                    content={content}
                    onChange={setContent}
                    transitionStates={transitionStates}
                    threadForm={threadForm}
                    disableTagAndTitleFields={disableTagAndTitleFields}
                    entityType={entityType}
                    entityId={entityId}
                    mode="Edit"
                    cancelButton={
                      <CancelThreadButton
                        handleChangeSelectedThread={handleChangeSelectedThread}
                        setContent={setContent}
                        commentData={commentData}
                      />
                    }
                    submitButton={
                      <SubmitThreadButton
                        handleChangeSelectedThread={handleChangeSelectedThread}
                        commentData={commentData}
                        index={index}
                        handleSubmit={handleSubmit}
                      />
                    }
                  />
                </Box>
              ) : (
                <Box>
                  <TextEditor
                    userList={userList}
                    content={content}
                    onChange={setContent}
                    transitionStates={transitionStates}
                    threadForm={threadForm}
                    readOnly={true}
                    disableTagAndTitleFields={disableTagAndTitleFields}
                    entityType={entityType}
                    entityId={entityId}
                  />
                </Box>
              )}
            </Box>

            <Box>
              <Stack direction="row" mt={1}>
                {renderReadModeActions()}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

ThreadMessage.propTypes = {
  selectedThread: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  transitionStates: PropTypes.arrayOf(PropTypes.string).isRequired,
  deleteNoteHandler: PropTypes.func.isRequired,
  isComponentFromLifecycleEvents: PropTypes.bool.isRequired,
  isUserAuthorizedForDeleteNote: PropTypes.bool.isRequired,
  isUserAuthorizedForUpdateNote: PropTypes.bool.isRequired,
  handleChangeSelectedThread: PropTypes.func.isRequired,
  handleOpenThreadChatPanel: PropTypes.func.isRequired,
  commentData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  userList: PropTypes.oneOfType([PropTypes.array]).isRequired,
  entityId: PropTypes.number.isRequired,
  entityType: PropTypes.string.isRequired,
  editNote: PropTypes.func.isRequired,
  getAllComments: PropTypes.func.isRequired,
  disableTagAndTitleFields: PropTypes.bool.isRequired,
  replyThread: PropTypes.bool.isRequired,
};

export default ThreadMessage;
